import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { useLang } from "../i18n";
import { ChangesSection, ExecutiveSummary, IdentitySection, PlanningSection, SourceHealthSection } from "../components/report/ReportNarrative";
import { FindingSection } from "../components/report/ReportFindings";
import { AppendixSection, IntegrityBlock, ProvenanceSection, ReportHeader } from "../components/report/ReportShell";

const DOMAIN_SECTIONS = [
  ["flood", "dd.flood"],
  ["heritage", "dd.heritage"],
  ["elevation", "dd.elevation"],
  ["terrain_water", "dd.terrainWater"],
  ["soils", "dd.soils"],
  ["gdos", "dd.gdos"],
  ["midas", "dd.midas"],
  ["mphp", "dd.mphp"],
];

export default function DueDiligencePage() {
  const { id, ppId } = useParams();
  const { t } = useLang();
  const [model, setModel] = useState(null);
  const [err, setErr] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState(false);
  const [parcelId, setParcelId] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [genErr, setGenErr] = useState(null);

  const load = () => {
    setErr(null);
    api.get(`/portfolios/${id}/parcels/${ppId}/dd-report`)
      .then((r) => setModel(r.data))
      .catch((e) => setErr(e.response?.data?.detail?.code || "error"));
  };

  useEffect(() => {
    setModel(null);
    load();
  }, [id, ppId]);

  useEffect(() => {
    if (err !== "no_report") return;
    api.get(`/portfolios/${id}`).then((r) => {
      const pp = (r.data.parcels || []).find((x) => x.id === ppId);
      if (pp) setParcelId(pp.parcel_id);
    }).catch(() => {});
  }, [err, id, ppId]);

  const generateReport = async () => {
    setGenBusy(true);
    setGenErr(null);
    try {
      await api.post("/reports", { parcel_id: parcelId, portfolio_id: id });
      setModel(null);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      setGenErr(typeof det === "object" ? (det.message || det.code) : (det || "error"));
    } finally {
      setGenBusy(false);
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfErr(false);
    try {
      const r = await api.post(`/portfolios/${id}/parcels/${ppId}/dd-export`);
      const d = await api.get(`/pdf-exports/${r.data.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([d.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PlanGmina-DD-${model.identity.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setPdfErr(true);
    } finally {
      setPdfBusy(false);
    }
  };

  const back = <Link to={`/portfolio/${id}`} className="text-xs text-emerald-700 hover:underline" data-testid="dd-back">{t("dd.back")}</Link>;

  if (err) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16" data-testid="dd-error">
        {back}
        {err === "no_report" ? (
          <div className="mt-6 rounded-xl border-2 border-slate-900 bg-white p-6" data-testid="dd-generate-card">
            <p className="font-heading text-lg font-semibold">{t("dd.noReport")}</p>
            <p className="mt-1 text-sm text-slate-500">{t("dd.generateDesc")}</p>
            <button onClick={generateReport} disabled={genBusy || !parcelId} data-testid="dd-generate-button"
              className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-slate-700 disabled:opacity-50">
              {genBusy ? t("dd.generating") : t("dd.generate")}
            </button>
            {genErr && <p className="mt-2 text-xs text-red-600" data-testid="dd-generate-error">{genErr}</p>}
          </div>
        ) : (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-700">
            {err === "no_export_access" ? t("dd.noAccess") : t("dd.error")}
          </div>
        )}
      </div>
    );
  }
  if (!model) {
    return <div className="mx-auto max-w-2xl px-4 py-16 text-sm text-slate-500" data-testid="dd-loading">{t("dd.loading")}</div>;
  }

  const findings = model.findings || {};
  const extraKeys = Object.keys(findings).filter(
    (k) => k !== "planning_registry" && !DOMAIN_SECTIONS.some(([dk]) => dk === k));

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="dd-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {back}
        <button onClick={downloadPdf} disabled={pdfBusy} data-testid="dd-pdf-download"
          className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-slate-700 disabled:opacity-50">
          {pdfBusy ? t("dd.pdfWorking") : t("dd.pdf")}
        </button>
      </div>
      {pdfErr && <div className="mt-2 text-xs text-red-600" data-testid="dd-pdf-error">{t("dd.pdfFailed")}</div>}

      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5 sm:p-8">
        <ReportHeader model={model} t={t} />

        <section className="mt-8" data-testid="dd-section-executive">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("dd.exec")}</h2>
          <div className="mt-3"><ExecutiveSummary model={model} /></div>
        </section>

        <section className="mt-8" data-testid="dd-section-identity">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("dd.identity")}</h2>
          <div className="mt-3"><IdentitySection model={model} t={t} /></div>
        </section>

        <section className="mt-8" data-testid="dd-section-planning">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("dd.planning")}</h2>
          <div className="mt-3 space-y-3">
            <PlanningSection model={model} t={t} />
            {findings.planning_registry && (
              <FindingSection f={findings.planning_registry} t={t} testId="dd-finding-planning_registry" />
            )}
          </div>
        </section>

        {DOMAIN_SECTIONS.map(([key, titleKey]) => findings[key] && (
          <section key={key} className="mt-8" data-testid={`dd-section-${key}`}>
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t(titleKey)}</h2>
            <div className="mt-3"><FindingSection f={findings[key]} t={t} testId={`dd-finding-${key}`} /></div>
          </section>
        ))}

        {extraKeys.map((k) => (
          <section key={k} className="mt-8" data-testid={`dd-section-${k}`}>
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{findings[k].title_pl}</h2>
            <div className="mt-3"><FindingSection f={findings[k]} t={t} testId={`dd-finding-${k}`} /></div>
          </section>
        ))}

        <section className="mt-8" data-testid="dd-section-changes">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("dd.changes")}</h2>
          <div className="mt-3"><ChangesSection model={model} t={t} /></div>
        </section>

        <section className="mt-8" data-testid="dd-section-source-health">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("dd.sourceHealth")}</h2>
          <div className="mt-3"><SourceHealthSection model={model} t={t} /></div>
        </section>

        <section className="mt-8" data-testid="dd-section-provenance">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("dd.provenance")}</h2>
          <div className="mt-3"><ProvenanceSection model={model} t={t} /></div>
        </section>

        <section className="mt-8" data-testid="dd-section-appendix">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("dd.appendix")}</h2>
          <div className="mt-3"><AppendixSection model={model} t={t} /></div>
        </section>

        <IntegrityBlock model={model} t={t} />
      </div>
    </div>
  );
}
